import {
  CompositeCalloutProps,
  TwilioHelpCenterUser,
  ValueOf,
} from "@twiliointernal/e2-commons-shared-definitions";
import { phoneNumbersModalTypes } from "@twiliointernal/e2-commons-shared-ui";
import { ChangeEvent } from "react";
import { Control, FieldError } from "react-hook-form";

export interface CurrentPlanProps {
  hasError: boolean;
  loading: boolean;
  otherPlansUrl: string;
  supportPlan?: SupportPlanResponse;
  pendingPlan?: SupportPlanResponse;
}

export interface SupportPlan {
  billableItemSid: SupportPlanBillableItemSidsType;
  brand?: SupportPlanBrand;
  description: string;
  displayName: string;
  friendlyName: string;
  level: SupportLevel;
  overview?: string;
  phoneNumbers: phoneNumbersModalTypes.PhoneNumberDetails[];
  price: number;
  pricePercent: number;
  shortDisplayName?: string;
  tag?: string;
}

export interface SupportPlanResponse extends SupportPlan {
  endDate: string | null;
  startDate: string;
  status: SupportPlanStatus;
}

export enum SupportPlanStatus {
  active = "active",
  pending = "pending",
}

export enum SupportPlanBrand {
  sendGrid = "sg",
  twilio = "tw",
}

export interface SupportLevel {
  key: string;
  tag: string;
}

export const SupportPlanBillableItemSids = {
  developer: "BIdeveloperSupport",
  bootstrap: "BIeb42386c52bbba15168d95967a134486",
  bootstrapAnnualPre2018: "BI6fdcc456e164344d1a7677f1f0752e14",
  bootstrapOld: "BIac31b4b41a7540a984e8a68ead12a530",
  business: "BIceadf727df10952652bae76a26d43f77",
  doGood: "BIf44d1c5612bfe7503f256e6e21d38f59",
  partnerAdvanced: "BIba7db56115701aa106f8ec958c7a0a61",
  personalized: "BIb84abcf4eed7773c71c4365e21b32ce9",
  personalizedPartnerMso: "BI961b38eb29b07b4cfe1d75f3ab4da979",
  personalizedPartnerMss: "BIb270fd45485f9816cd3967691b870a8c",
  personalizedPre2018: "BId24ea45931375ef6aee9ad91f67e31aa",
  premium: "BI8d5215fedc19839fa6ade96b25b10d47",
  premiumAnnualPre2018: "BI9ce6b0aa534e735c210c6856504d20d8",
  premiumComplimentaryPre2018: "BIada0a7f499b46bc4f33d5dfeb98bd246",
  premiumPhone: "BI01baad4736d34b79ae729691e2dcdcb2",
  premiumPresalesPre2018: "BI55bda356dd494b96a989f9830f6da957",
  premium24x7: "BI7af2924c7cb04e1d864f45978ad9a7bd",
  production: "BIc01c5cdd547a811e081aa9232ec6e254",
  sendGridPersonalized: "BIsendGridPersonalizedPlan",
} as const;
export type SupportPlanBillableItemSidsType = ValueOf<
  typeof SupportPlanBillableItemSids
>;

export interface ConfirmationDetails {
  supportPlan: string;
  startDate: string;
}

export interface FormErrors {
  email?: FieldError;
  firstName?: FieldError;
  lastName?: FieldError;
  phoneNumber?: FieldError;
}

export interface ModalBodyContentProps {
  authorizationStatement?: string;
  control: Control<PersonalizedSupportFormValues>;
  errors: FormErrors;
  getValues: (key: string) => string;
  heading: string;
  isPersonalizedSupport: boolean;
  planIncludes: string[];
  pricesPerMonth: PricesPerMonth;
  startDate: string;
  startDateHasError: boolean;
  todaysDate: string;
  updateStartDate: (e: ChangeEvent<HTMLInputElement>) => void;
}

export interface ModalContentProps {
  authorizationStatement: string;
  ctaText: string;
  displayName: string;
  planIncludes: string[];
  pricesPerMonth: PricesPerMonth;
}

export interface OnToggleModalOptions {
  isOpen: boolean;
  supportPlan?: string;
}

export enum PersonalizedSupportFormValueType {
  email = "email",
  firstName = "firstName",
  lastName = "lastName",
  phoneNumber = "phoneNumber",
}

export interface PersonalizedSupportFormValues {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export interface SupportPlanDetails {
  authorizationStatement: string;
  ctaText: string;
  displayName: string;
  dutyManager: boolean;
  friendlyName: string;
  modalPlanDescription: string;
  namedContact: boolean;
  planIncludes: string[];
  phoneSupport: boolean;
  guaranteedResponse: boolean;
  apiNotifications: boolean;
  liveChat: boolean;
  pricesPerMonth: PricesPerMonth;
  quarterlyReview: boolean;
  shortDisplayName: string;
}

export type SupportPlansDetailsType = Record<
  TwilioSupportPlansVariant,
  SupportPlanDetails
>;

export interface PricesPerMonth {
  primary: string;
  secondary: string;
}

export interface RenderIconProps {
  iconVariant: SupportPlansTableIconVariant;
  onClick?: () => void;
  tooltipText?: string;
}

export interface SelectSupportPlanModalState {
  isSelectSupportPlanModalOpen: boolean;
  modalSupportPlan?: string;
}

export interface SelectSupportPlansModalProps {
  ctaOptions?: CompositeCalloutProps;
  isOpen: boolean;
  onBuySupportPlan: (
    billableItemSid: string,
    startDate: string
  ) => Promise<void>;
  onClose: () => void;
  onError: (errorMessage: string) => void;
  onSendContactInfo: (
    formValues: PersonalizedSupportFormValues
  ) => Promise<void>;
  supportPlansDetails: SupportPlansDetailsType;
  supportPlan: string;
  trackingMethods: SupportPlansTrackingMethods;
  user: SupportPlanUser;
}

export interface SupportPlanDescriptionModalProps {
  heading: string;
  isOpen: boolean;
  onCloseModal: () => void;
  supportPlansDetails: SupportPlansDetailsType;
}

export interface SupportPlanDescriptionModalState {
  heading?: string;
  isOpen: boolean;
}

export enum SupportPlansTableIconVariant {
  accept = "accept",
  info = "info",
  minus = "minus",
}

export interface SupportPlansTableRows {
  [key: string]: SupportPlanTableCell[];
}

export interface SupportPlansTableRowsProps {
  currentPlanShortDisplayName?: string;
  hasPersonalizedSupport: boolean;
  hasSelectButtons: boolean;
  useFeatureIcons: boolean;
  openSelectSupportPlanModal: (supportPlan: string) => void;
  openDescriptionModal: (heading: string) => void;
  tableHeaders: string[];
  tableRows: SupportPlansTableRows;
}

export type SupportPlanUser = Omit<TwilioHelpCenterUser, "fullName">;

export enum TwilioSupportPlansVariant {
  Business = "Business",
  Developer = "Developer",
  Personalized = "Personalized",
  Production = "Production",
}

export interface SupportPlansViewFooterProps {
  footNotes: string[];
}

export interface SupportPlansViewProps {
  currentPlan: CurrentPlanProps;
  ctaOptions?: CompositeCalloutProps;
  footer: SupportPlansViewFooterProps;
  hasPersonalizedSupport?: boolean;
  hasSelectButtons: boolean;
  onBuySupportPlan: (supportPlan: string, startDate: string) => Promise<void>;
  onSendContactInfo: (
    formValues: PersonalizedSupportFormValues
  ) => Promise<void>;
  selectSupportPlanModalCtaOptions?: CompositeCalloutProps;
  supportPlansDetails: SupportPlansDetailsType;
  tableHeaders: string[];
  tableRows: SupportPlansTableRows;
  trackingMethods: SupportPlansTrackingMethods;
  useFeatureIcons: boolean;
  user: SupportPlanUser;
}

export interface SupportPlanTableCell {
  infoIconToolText?: string;
  primaryText: string;
  secondaryText: string;
  serviceIncluded?: boolean;
}

export interface SupportPlansTrackingMethods {
  buyButtonClick: (supportPlan: string) => void;
  cancelButtonClick: (supportPlan: string) => void;
  sendContactInfoButtonClick: () => void;
  supportPlanModalOpen: (supportPlan: string) => void;
}
